import React, { useEffect } from "react";
import { bool } from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import MainContainer from "pages/_components/MainContainer";
import { ResponsiveHeader, DesktopButtons } from "pages/_components/header";
import { Field, Form, Formik } from "formik";
import I18n from "pages/_components/I18n";
import { Link } from "react-router-dom";
import * as i18n from "util/i18n";
import Selector from "pages/_components/fields/formik/Selector";
import useGetDebinAccounts from "hooks/useDebin";
import Beneficiaries from "pages/debinPayments/_components/Beneficiaries";
import TextField from "pages/_components/fields/TextField";
import Button from "pages/_components/Button";
import { actions as debinActions, types } from "reducers/debin";
import * as Yup from "yup";
import { routerActions } from "connected-react-router";
import useSchema from "hooks/useSchema";
import ProductSelector from "pages/_components/fields/formik/FormikProductSelector";
import Notification from "pages/_components/Notification";
import AmountField from "pages/_components/fields/formik/AmountField";
import { actions as notificationActions } from "reducers/notification";
import GridTable from "pages/_components/GridTable/GridTable";

const FORM_ID = "debin.solicitDebin";

const SolicitDebin = ({ isDesktop }) => {
    const { stringRequired, numberRequired } = useSchema();
    const dispatch = useDispatch();
    const { beneficiary, concepts, solicitDebin } = useSelector((state) => state.debin);
    const { debinAccounts, fetching, fetchAccounts } = useGetDebinAccounts();
    const debinAccountsAr = debinAccounts.filter((item) => item.currency === "ARS");
    const hasBeneficiary = beneficiary?.cbu;
    const initialValues = {
        debinAccount: solicitDebin?.debinAccount?.productId || "",
        beneficiary: solicitDebin?.beneficiary || "",
        concept: solicitDebin?.concept || "VAR",
        validity: solicitDebin?.validity || "",
        description: solicitDebin?.description || "",
        amountField: {
            amount: solicitDebin?.amountField?.amount || null,
            currency: solicitDebin?.amountField?.currency || "ARS",
        },
    };

    const currencies = [
        { id: "ARS", label: i18n.get(`currency.label.ARS`) },
        { id: "USD", label: i18n.get(`currency.label.USD`) },
    ];

    const conceptsOptions = () =>
        concepts.map((concept) => ({
            value: concept?.id,
            label: `${concept?.id} - ${concept?.name}`,
        }));

    const maskAccountNumber = (accountNumber) => {
        const mask = "••••";
        if (accountNumber) {
            return mask + accountNumber.slice(-4);
        }
        return mask;
    };

    const getLabel = (account) => {
        if (account) {
            return `${i18n.get(`accounts.accountType.${account?.accountType}`)} ${maskAccountNumber(
                account?.accountNumber,
            )} ${account?.accountType} ${account?.currency}`;
        }

        return i18n.get("accounts.check.empty", "No tenés cuentas para realizar la solicitud");
    };

    useEffect(() => {
        fetchAccounts();
    }, []);

    const accountDebinOptions = () =>
        debinAccounts.map((account) => ({
            balance: { currency: account?.currency, quantity: account?.availableBalance },
            currency: account?.currency,
            frequentDestination: null,
            id: account?.productId,
            label: getLabel(account),
            minimumPayment: 0,
            permissions: Object.keys(account?.permissions),
            statementsLoan: null,
            totalPayment: 0,
            type: account?.accountType,
        }));

    const getAccount = (accountID) => debinAccounts.find((account) => account?.productId === accountID);

    const handleChangeAccount = (id, values, setFieldValue) => {
        const selected = debinAccounts.find((_account) => _account.productId === id);
        if (setFieldValue) {
            setFieldValue("amountField.currency", selected.currency);
        }
        dispatch(
            debinActions.setSolicitDebin({
                ...values,
                amount: values.amountField.amount,
                debinAccount: getAccount(id),
            }),
        );
    };

    const handleSubmit = (values) => {
        const conceptLabel = concepts.find((object) => object.id === values.concept);

        const account = getAccount(values.debinAccount);
        if (beneficiary.cbu === account.cbu) {
            dispatch(
                notificationActions.showNotification(i18n.get("debin.cbu.error.message"), "error", ["debinPayments"]),
            );
        } else {
            dispatch(
                debinActions.setSolicitDebin({
                    ...values,
                    amount: values.amountField.amount,
                    debinAccount: getAccount(values?.debinAccount),
                    beneficiary,
                    conceptLabel,
                }),
            );
            dispatch(routerActions.push("/debinPayments/solicitDebin/confimation"));
        }
    };

    const debinAccountsValidation = Yup.lazy(() => {
        const validate = {
            amountField: Yup.object()
                .shape({
                    amount: numberRequired,
                })
                .test(
                    "availableAmount",
                    i18n.get("transfer.amount.error"), // eslint-disable-next-line func-names
                    function(value) {
                        // eslint-disable-next-line react/no-this-in-sfc
                        const { debinAccount = "" } = this.parent;
                        const selected = debinAccounts.find((_account) => _account.productId === debinAccount);
                        if (value.amount > selected?.availableBalance) {
                            dispatch(
                                notificationActions.showNotification(
                                    i18n.get("debin.insuficient.amount.error"),
                                    "error",
                                    ["debinPayments"],
                                ),
                            );
                        }
                        return value.amount <= selected?.availableBalance;
                    },
                ),
            concept: stringRequired,
            validity: stringRequired,
        };
        return Yup.object().shape(validate);
    });

    return (
        <>
            <Notification scopeToShow="debinPayments" />
            <ResponsiveHeader id="solicit.debin.title" backLinkTo="/debinPayments">
                <DesktopButtons>
                    <Link className="btn btn-primary btn-block" to="/debinPayments/addAccount">
                        <I18n id="debin.AddAccount" />
                    </Link>
                </DesktopButtons>
            </ResponsiveHeader>
            <MainContainer showLoader={fetching}>
                <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={debinAccountsValidation}>
                    {({ values, setFieldValue }) => (
                        <Form>
                            <GridTable className="form transfer-others">
                                <GridTable.Container columns={12} columnsTemplate="fixed">
                                    <GridTable.Data columnStart={2} columnWidth={10}>
                                        <Field
                                            component={ProductSelector}
                                            idForm={FORM_ID}
                                            name="debinAccount"
                                            label={i18n.get("debin.credit.account")}
                                            placeholder={debinAccountsAr && getLabel(debinAccountsAr[0])}
                                            data={{ emptyOptionLabel: "", options: accountDebinOptions() }}
                                            onChange={(id) => handleChangeAccount(id, values, setFieldValue)}
                                        />
                                    </GridTable.Data>
                                </GridTable.Container>
                                <Beneficiaries
                                    isDesktop={isDesktop}
                                    idForm={FORM_ID}
                                    setEmailValues={setFieldValue}
                                    beneficiaryData={beneficiary}
                                    actionType={types.SELECT_BENEFICIARY}
                                />
                                <GridTable.Container columns={12} columnsTemplate="fixed">
                                    <GridTable.Data columnStart={2} columnWidth={5}>
                                        <Field
                                            component={AmountField}
                                            idForm={FORM_ID}
                                            name="amountField"
                                            labelText={i18n.get("debin.solicitDebin.amount.label")}
                                            data={{ options: currencies }}
                                            clearable={false}
                                            maxLength={16}
                                            disable={!hasBeneficiary}
                                            disableSelect
                                            hideCurrency
                                            fixedDecimalScale
                                            placeholder={i18n.get("debin.solicitDebin.amount.placeholder")}
                                            prefix={i18n.get(`currency.label.ARS`)}
                                        />
                                    </GridTable.Data>
                                    <GridTable.Data columnStart={7} columnWidth={5}>
                                        <Field
                                            component={Selector}
                                            idForm="debin.addrecurence"
                                            name="concept"
                                            label={i18n.get("debin.concept.label")}
                                            options={conceptsOptions()}
                                            placeholder={`${i18n.get("global.select")}`}
                                            disabled={!hasBeneficiary}
                                        />
                                    </GridTable.Data>
                                    <GridTable.Data columnStart={2} columnWidth={5}>
                                        <Field
                                            component={Selector}
                                            idForm={FORM_ID}
                                            name="validity"
                                            label={i18n.get("debin.solicitDebin.validity.label")}
                                            options={[
                                                { value: "24 horas", label: "24 horas" },
                                                { value: "48 horas", label: "48 horas" },
                                                { value: "72 horas", label: "72 horas" },
                                            ]}
                                            placeholder={`${i18n.get("global.select")}`}
                                            disabled={!hasBeneficiary}
                                        />
                                    </GridTable.Data>
                                    <GridTable.Data columnStart={7} columnWidth={5}>
                                        <Field
                                            component={TextField}
                                            idForm="debin.addrecurence"
                                            name="description"
                                            labelnoI18n={i18n.get("debin.addrecurence.description.label")}
                                            hidePlaceholder
                                            disabled={!hasBeneficiary}
                                        />
                                    </GridTable.Data>
                                </GridTable.Container>
                            </GridTable>

                            <GridTable>
                                <GridTable.Container columns={12} columnsTemplate="fixed" className="form-actions">
                                    <GridTable.Data columnStart={isDesktop ? 5 : 1} columnWidth={isDesktop ? 4 : 12}>
                                        <Button
                                            type="submit"
                                            className="btn-primary"
                                            label="global.continue"
                                            disabled={
                                                !hasBeneficiary ||
                                                !values.debinAccount ||
                                                !values.amountField.amount ||
                                                !values.concept ||
                                                !values.validity
                                            }
                                        />
                                    </GridTable.Data>
                                </GridTable.Container>
                            </GridTable>
                            {/* <Row className="form-actions">
                                <Col xs={12} md={4} mdOffset={4}>
                                    <Button
                                        type="submit"
                                        className="btn-primary"
                                        label="global.continue"
                                        disabled={
                                            !hasBeneficiary ||
                                            !values.debinAccount ||
                                            !values.amountField.amount ||
                                            !values.concept ||
                                            !values.validity
                                        }
                                    />
                                </Col>
                            </Row> */}
                        </Form>
                    )}
                </Formik>
            </MainContainer>
        </>
    );
};

SolicitDebin.propTypes = {
    isDesktop: bool.isRequired,
};

export default SolicitDebin;
